/**
 * EngagementsCalendrier — Timeline 12 mois glissants avec pins préavis/échéance.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { getCategoryMeta, fmtEur, fmtDate, fmtDateShort } from "@/lib/engagements-categories";

export default function EngagementsCalendrier() {
  const navigate = useNavigate();
  const [data, setData] = useState({ kpis: {}, echeances: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/engagements/echeances");
        setData(r.data);
      } catch (e) { toast.error(apiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    months.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" }),
    });
  }

  const getMonth = (s) => s ? `${new Date(s).getFullYear()}-${String(new Date(s).getMonth() + 1).padStart(2, "0")}` : null;

  return (
    <div className="space-y-6" data-testid="engagements-calendrier-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Calendrier des échéances</h1>
        <p className="text-sm text-slate-500">Vue 12 mois glissants sur tous vos engagements</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard label="Préavis ce mois" value={data.kpis?.preavis_ce_mois || 0} color="blue" />
        <KpiCard label="Dans les 30 jours" value={data.kpis?.preavis_30_jours || 0} color="orange" />
        <KpiCard label="Dans les 60 jours" value={data.kpis?.preavis_60_jours || 0} color="red" />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calendar className="w-4 h-4" />Chronologie 12 mois</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p className="text-slate-500 text-center py-6">Chargement...</p> :
           data.echeances.length === 0 ? <p className="text-slate-500 text-center py-6">Aucune échéance</p> : (
            <>
              <div className="flex border-b mb-4 pb-2 overflow-x-auto">
                <div className="w-56 shrink-0" />
                {months.map(m => (
                  <div key={m.key} className="min-w-[64px] flex-1 text-center text-[10px] font-medium text-slate-500 uppercase">{m.label}</div>
                ))}
              </div>
              <div className="space-y-2">
                {data.echeances.map((c, i) => {
                  const meta = getCategoryMeta(c.categorie);
                  const Icon = meta.icon;
                  const pm = getMonth(c.date_limite_preavis);
                  const em = getMonth(c.date_echeance);
                  return (
                    <motion.div key={c.contrat_id} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.02 }}
                                     className="flex items-center hover:bg-slate-50 rounded-lg cursor-pointer overflow-x-auto"
                                     onClick={() => navigate(`/app/depenses/contrats/${c.contrat_id}`)} data-testid={`row-${c.contrat_id}`}>
                      <div className="w-56 shrink-0 flex items-center gap-3 pr-4 py-2">
                        <div className={`w-8 h-8 rounded ${meta.bg} flex items-center justify-center shrink-0`}>
                          <Icon className={`w-4 h-4 ${meta.color}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{c.prestataire}</p>
                          <p className="text-xs text-slate-500 truncate">{c.nom_contrat}</p>
                        </div>
                      </div>
                      <div className="flex-1 flex h-10 items-center">
                        {months.map(m => {
                          const isP = pm === m.key, isE = em === m.key;
                          return (
                            <div key={m.key} className="min-w-[64px] flex-1 flex items-center justify-center relative">
                              {isP && <div className="w-3 h-3 rounded-full bg-red-500 border-2 border-white shadow" title={`Préavis: ${fmtDateShort(c.date_limite_preavis)}`} />}
                              {isE && !isP && <div className="w-3 h-3 rounded-full bg-blue-400 border-2 border-white shadow" title={`Échéance: ${fmtDateShort(c.date_echeance)}`} />}
                            </div>
                          );
                        })}
                      </div>
                      <div className="w-28 shrink-0 text-right pr-2 py-2">
                        <p className="text-sm font-medium font-mono">{fmtEur(c.montant_mensuel_ht)}<span className="text-xs text-slate-400">/m</span></p>
                        {c.jours_restants != null && (
                          <Badge className={c.jours_restants <= 7 ? "bg-red-100 text-red-700" : c.jours_restants <= 30 ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-600"}>
                            <Clock className="w-3 h-3 mr-1" />{c.jours_restants}j
                          </Badge>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                    </motion.div>
                  );
                })}
              </div>
              <div className="flex items-center gap-6 mt-6 pt-4 border-t text-xs text-slate-500">
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-red-500" />Date limite préavis</div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-blue-400" />Date d'échéance</div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ label, value, color }) {
  const bg = color === "blue" ? "from-blue-50" : color === "orange" ? "from-orange-50" : "from-red-50";
  const text = color === "blue" ? "text-blue-600" : color === "orange" ? "text-orange-600" : "text-red-600";
  return (
    <Card className={`bg-gradient-to-br ${bg} to-white`}>
      <CardContent className="p-4">
        <p className="text-xs text-slate-500 uppercase">{label}</p>
        <p className={`text-3xl font-bold ${text} font-mono`}>{value}</p>
      </CardContent>
    </Card>
  );
}
